import styled from '@emotion/styled'
import { debounce } from 'lodash-es'
import {
    memo,
    SyntheticEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
} from 'react'
import { openMenuDialog } from '../../../../actions/menu'
import {
    AppStateType,
    ListType,
    SettingsType,
} from '../../../../constants/enums'
import { QUICK_REPLY } from '../../../../constants/floaters'
import { isLounge } from '../../../../mfcLogic/userProperties/user'
import { useAppStateSelector } from '../../../Hooks/useAppState'
import { useHealthAppStateSelector } from '../../../Hooks/useHealthAppState'
import { useLayoutSelector } from '../../../Hooks/useLayout'
import { useList } from '../../../Hooks/useList'
import CircularProgress from '../../CircularProgress'

import { generateModalKey } from '../../../../state/modals'
import { useFloater } from '../../../Hooks/useModal'
import { usePortalStateSelector } from '../../../Hooks/usePortalState'
import { useRoomSelector } from '../../../Hooks/useRoomAppState'
import { useSettingSelector } from '../../../Hooks/useSettings'
import { useUserSelector } from '../../../Hooks/useUser'
import { palette } from '../../../styles/colors'
import { PAPER_HEIGHTS } from '../../../theme'
import { filterMessages } from '../common/utilities'
import LoungePreviews from './LoungePreviews'
import PreviewCount from './PreviewCount'
import RoomMessages from './RoomMessages'
import TipSlider from './TipSlider'
import ViewerCount from './ViewerCount'

const ChatRoot = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    overflow: 'hidden',
    zIndex: PAPER_HEIGHTS.zero,
    color: palette.contrastText,
    background: palette.pinnedChatBackground,
})

const ChatLoading = styled('div')({
    display: 'flex',
    height: '100%',
    width: '100%',
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: PAPER_HEIGHTS.zero,
})

const StyledChatTitle = styled('div')({
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    justifyContent: 'center',
    height: 40,
    minHeight: 40,
    maxHeight: 40,
    '& p': {
        fontSize: 14,
        fontWeight: 600,
    },
})

const MessagesContainer = styled('div')({
    width: '100%',
    height: '100%',
    overflowX: 'hidden',
    overflowY: 'auto',
    touchAction: 'auto',
    zIndex: PAPER_HEIGHTS.zero,
    WebkitUserDrag: 'auto',
    msOverflowStyle: 'none' as any,
    scrollbarWidth: 'none' as any,
    '&::-webkit-scrollbar': {
        display: 'none',
    },
})

interface ChatTitleProps {
    title: string
}

const ChatTitle = memo(({ title }: ChatTitleProps) => (
    <StyledChatTitle>
        <p>{title}</p>
    </StyledChatTitle>
))

export type RoomChatProps = {
    id: number
    showLoungePreviews?: boolean
    pinned?: boolean
    showingTips?: boolean
    height?: number
    styleHeight?: number | string
    styleDisplay?: string

    onOpenWhiteboard?(identifier: string | number): void
    setLoungePreviews?(cb?: (prev: boolean) => boolean): void
    onTipToggle?(): void
}

const Chat = memo((props: RoomChatProps) => {
    const friendsListIds = useList(ListType.FRIENDS, { returnIDs: true })
    const {
        id,
        styleDisplay,
        styleHeight,
        height,
        pinned = false,
        showingTips = false,
        showLoungePreviews = false,
        onOpenWhiteboard = () => {},
        setLoungePreviews = () => {},
        onTipToggle,
    } = props

    const darkMode = useAppStateSelector(AppStateType.APP, 'darkMode')
    const [news] = useSettingSelector(SettingsType.NEWS_NOTIFICATIONS, 'news')
    const socketConnected = useHealthAppStateSelector('connected')
    const fullWindow = useLayoutSelector('fullWindow')
    const composeFocused = usePortalStateSelector('composeFocused')
    const quietFocus = usePortalStateSelector('quietFocus')
    const usableComposeFocused = composeFocused && !quietFocus

    const ref = useRef<HTMLDivElement>(undefined)
    const canAutoScroll = useRef<boolean>(true)
    const fullyLoaded = useRef<boolean>(false)
    const forcedScrolling = useRef<boolean>(false)
    const loadTimeout = useRef(null)

    const participants = useRoomSelector(id, 'participants')
    const list = useRoomSelector(id, 'messages')
    const loungeSurrogateID = useRoomSelector(id, 'loungeSurrogateID')

    const userBadges = useUserSelector(id, 'userBadges')
    const emojiClubs = useUserSelector(id, 'emojiClubs')
    const streamType = useUserSelector(id, 'streamType')
    const isClubShow = useUserSelector(id, 'isClubShow')
    const isOffline = useUserSelector(id, 'isOffline')
    const slug = useUserSelector(id, 'slug')

    const lounge = isLounge(slug)
    const hasParticipants = participants?.length > 0

    const [quickReply] = useFloater(generateModalKey(QUICK_REPLY, id))
    const isQuickReplyOpen =
        quickReply && quickReply.show && !quickReply.state.minimized

    const messages = useMemo(
        () =>
            filterMessages(
                list,
                news,
                streamType,
                // showInlinePM,
                friendsListIds,
                showingTips,
            ),
        [
            list,
            news,
            streamType,
            /* showInlinePM, */ friendsListIds,
            showingTips,
        ],
    )
    const hasMessages = messages?.length > 0
    const joinedRoom = !!list || hasParticipants
    const successfullyJoined = joinedRoom || hasMessages
    const loading = !(socketConnected && joinedRoom)

    useEffect(() => clearTimeout(loadTimeout.current), [])

    useEffect(() => {
        if (!successfullyJoined) return
        clearTimeout(loadTimeout.current)
        if (fullyLoaded.current) return
        loadTimeout.current = setTimeout(() => {
            if (fullyLoaded.current) return
            scrollToBottom()
            fullyLoaded.current = true
        }, 1500)
        return () => {
            fullyLoaded.current = false
        }
    }, [successfullyJoined])

    useEffect(() => {
        scrollToBottom()
    }, [messages, isQuickReplyOpen])

    const scrollToBottom = useCallback(() => {
        if (!ref.current) return
        if (canAutoScroll.current || !fullyLoaded.current) {
            // set variable when i scroll here and clear it in the onScroll
            // if the var is true then i shouldn't recalc canAutoScroll
            forcedScrolling.current = true
            ref.current.scroll?.({
                top: ref.current.scrollHeight,
                behavior: fullyLoaded.current ? 'smooth' : 'instant',
            })
        }
    }, [])

    const onLoad = useCallback((event: SyntheticEvent<HTMLImageElement>) => {
        const target = event.target as HTMLImageElement
        // this is pretty terrible, but
        const targetIsLoungePreview =
            target.getAttribute('data-testid') === 'Image__BroadcastPreview'
        if (lounge && targetIsLoungePreview) return
        if (canAutoScroll.current || !fullyLoaded.current) {
            ref.current.scroll?.({
                top: ref.current.scrollHeight + target.clientHeight,
                behavior: 'instant',
            })
        }
    }, [])

    const onOpenMenu = useCallback(
        (menuSlug: string) => {
            openMenuDialog(menuSlug, {
                noVideo: menuSlug !== slug,
                room: true,
            })
        },
        [slug],
    )
    const onLoungePreviewsToggle = useCallback(() => {
        setLoungePreviews((showLoungePreviews) => !showLoungePreviews)
    }, [showLoungePreviews])

    const onScroll = useCallback(
        debounce(() => {
            if (!forcedScrolling.current) {
                canAutoScroll.current =
                    ref.current?.scrollHeight -
                        ref.current?.scrollTop -
                        ref.current?.clientHeight <
                    30
            }
            forcedScrolling.current = false
        }, 120),
        [],
    )

    if (loading && (!isOffline || lounge)) {
        return (
            <ChatLoading
                data-testid="Chat__Pane"
                style={{ display: styleDisplay, height: styleHeight }}
            >
                <CircularProgress disableShrink />
            </ChatLoading>
        )
    }
    if (fullWindow) {
        return (
            <ChatRoot
                style={{
                    backgroundImage: palette.fullWindowChatBackground,
                    display: styleDisplay,
                    height: styleHeight,
                }}
                data-testid="Chat__Pane"
                className="FULL_WINDOW"
            >
                {/* [full-window] 'spacer' used to set the height of the chat panel */}
                <div
                    style={{
                        height: height < 600 ? '25%' : '33%',
                        width: '100%',
                    }}
                />
                <MessagesContainer
                    style={{
                        height: height < 600 ? '75%' : '67%',
                        WebkitMaskImage:
                            'linear-gradient(to top, black 0%, 95%, rgba(0,0,0,0) 100%)',
                        maskImage:
                            'linear-gradient(to top, black 0%, 95%, rgba(0,0,0,0) 100%)',
                    }}
                    onScroll={onScroll}
                    ref={ref}
                >
                    <RoomMessages
                        slug={slug}
                        fullWindow={fullWindow}
                        darkMode={darkMode}
                        isLounge={lounge}
                        pinned={pinned}
                        messages={messages}
                        userBadges={userBadges}
                        emojiClubs={emojiClubs}
                        clubshowActive={isClubShow}
                        onLoad={onLoad}
                        onOpenMenu={onOpenMenu}
                        onOpenWhiteboard={onOpenWhiteboard}
                    />
                </MessagesContainer>
            </ChatRoot>
        )
    }

    const inLoungeAndPreviewsOnly = showLoungePreviews && lounge
    return (
        <ChatRoot
            style={{
                display: styleDisplay,
                height: styleHeight,
            }}
            data-testid="Chat__Pane"
        >
            {showingTips && <ChatTitle title={'Recent Room Tips'} />}
            <TipSlider id={id} pushDown={showingTips} onClick={onTipToggle} />
            {!inLoungeAndPreviewsOnly &&
                !usableComposeFocused &&
                !showingTips && (
                    <ViewerCount
                        id={id}
                        pushDown={showingTips}
                        pinned={pinned}
                    />
                )}
            {lounge && (
                <PreviewCount
                    id={id}
                    onClick={onLoungePreviewsToggle}
                    shown={showLoungePreviews}
                />
            )}
            {inLoungeAndPreviewsOnly ? (
                <LoungePreviews
                    id={id}
                    loungeSlug={slug}
                    surrogateID={loungeSurrogateID}
                    fullWindow={fullWindow}
                    darkMode={darkMode}
                    // view={PreviewView.joinedMyml}
                />
            ) : (
                <MessagesContainer
                    onScroll={onScroll}
                    ref={ref}
                    id={`${pinned ? 'pinned-' : ''}room-chat-${id}`}
                >
                    <RoomMessages
                        slug={slug}
                        fullWindow={fullWindow}
                        darkMode={darkMode}
                        isLounge={lounge}
                        pinned={pinned}
                        messages={messages}
                        userBadges={userBadges}
                        emojiClubs={emojiClubs}
                        clubshowActive={isClubShow}
                        onLoad={onLoad}
                        onOpenMenu={onOpenMenu}
                        onOpenWhiteboard={onOpenWhiteboard}
                    />
                </MessagesContainer>
            )}
        </ChatRoot>
    )
})

export default Chat
